/**
 * Speaking the reply out loud, via `speechSynthesis`.
 *
 * Unlike recognition, this one runs on the device and needs no network. The catch is voices:
 * they are installed by the operating system, load asynchronously, and a machine that has no
 * Russian voice will happily read Russian text with an English one, which sounds like nonsense.
 * So the voice is chosen explicitly, and the caller is told when no matching voice exists —
 * better a silent reply than a mangled one.
 */

import { SPEECH_LOCALE } from '../core/language.js';
import type { Lang } from '../core/types.js';

export function isVoiceSupported(): boolean {
  return typeof globalThis !== 'undefined' && 'speechSynthesis' in globalThis;
}

/**
 * Voices populate asynchronously in most browsers: the first `getVoices()` after page load
 * usually returns an empty array, and a `voiceschanged` event follows.
 */
export function loadVoices(timeoutMs = 2000): Promise<SpeechSynthesisVoice[]> {
  if (!isVoiceSupported()) return Promise.resolve([]);
  const existing = window.speechSynthesis.getVoices();
  if (existing.length > 0) return Promise.resolve(existing);

  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(window.speechSynthesis.getVoices()), timeoutMs);
    window.speechSynthesis.addEventListener(
      'voiceschanged',
      () => {
        clearTimeout(timer);
        resolve(window.speechSynthesis.getVoices());
      },
      { once: true },
    );
  });
}

/**
 * Picks the best voice for a language.
 *
 * Exact locale first (`ru-RU`), then any voice whose tag starts with the language (`ru`), then
 * nothing. Returning `undefined` rather than a default is the point: the caller must be able to
 * tell that this machine cannot speak this language.
 */
export function pickVoice(
  voices: readonly SpeechSynthesisVoice[],
  lang: Lang,
): SpeechSynthesisVoice | undefined {
  const locale = SPEECH_LOCALE[lang].toLowerCase();
  const prefix = lang.toLowerCase();
  return (
    voices.find((v) => v.lang.toLowerCase() === locale) ??
    voices.find((v) => v.lang.toLowerCase().replace('_', '-').startsWith(prefix))
  );
}

export interface SpeakResult {
  spoken: boolean;
  /** Set when nothing was spoken, explaining why. */
  reason?: string;
}

/** Speaks `text`, cancelling anything already in progress. */
export async function speak(text: string, lang: Lang): Promise<SpeakResult> {
  if (!isVoiceSupported()) {
    return { spoken: false, reason: 'This browser cannot speak.' };
  }
  const voices = await loadVoices();
  const voice = pickVoice(voices, lang);
  if (!voice) {
    return {
      spoken: false,
      reason: `No ${lang === 'ru' ? 'Russian' : 'English'} voice is installed on this device.`,
    };
  }

  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.voice = voice;
  utterance.lang = voice.lang;
  utterance.rate = 1;
  utterance.pitch = 1;
  window.speechSynthesis.speak(utterance);
  return { spoken: true };
}

/** Stops whatever is being said. Safe to call when nothing is. */
export function stopSpeaking(): void {
  if (isVoiceSupported()) window.speechSynthesis.cancel();
}
